/**
 * @file iframe通信组件
 */
import {useIframeBridge} from "./iframe-provider";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {IframeBridge, type InjectApiOption} from "@baidu/code-live-utils";
import type {CodePreviewProps as CodeCorePreviewProps} from "@baidu/code-live-core";
import {ClientPlugins} from '@baidu/code-live-api/client';
import "./style.css";
import {useEditor} from "../hooks/use-editor";

type ApiCallback = (...args: any) => any;

type ApiOptions = InjectApiOption & {
    cb: ApiCallback
};

export interface SetupBridge {
    injectApi: (apiName: string, options: ApiCallback | ApiOptions) => void;
    injectComponents: (name: string, Component: string, css: string) => void;
}

export interface CodePreviewProps extends Omit<CodeCorePreviewProps, 'observerResizeElement'> {
    // iframe页面地址
    iframeUrl: string;
    // fixme 用于请求知识库KV存储时的id，目前比较耦合，后续找机会解耦
    id?: string;
    // 启用后，内部渲染的组件根元素上会多一个height: 100vh的样式；用于演示模式下的卡片随心搭撑满页面调优
    hScreen?: boolean;
    // iframe内部的`100vh`自适应宿主高度
    adaptHeight?: boolean;
    // 业务方监听iframe内的请求
    setup?: (bridge: SetupBridge) => void;
    passEmitter?: (emitter: any) => void;
    // 键盘事件
    onKeyboard?: (key?: string, options?: {atTop?: boolean; atBottom?: boolean;}) => void;
    // 监听滚轮事件；目前只有用户在非react组件，一般是蒙层上滚动，才会触发
    onWheel?: (options: {deltaY: number}) => void;
}

export interface ICodesandboxRef {
    cleanRangeTrigger?: () => Promise<any>;
}

export const CodeSandboxPreview = forwardRef<ICodesandboxRef, CodePreviewProps>((props, ref) => {
    const {
        iframeUrl,
        id: builderId,
        className,
        defaultCompiledComponent,
        compileTool,
        compiledComponent,
        code,
        renderProps,
        editor,
        hScreen,
        adaptHeight,
        onSizeChange,
        onError,
        onCompiled,
        onReady,
        onClick: onComponentClick,
        onErrorRetry,
        setup,
        showErrorRetry,
        passEmitter,
        onKeyboard,
        onWheel,
        ...restProps
    } = props;
    const iframeRef = useRef(null);
    
    const {
        bridgeOnMap,
        setupApiMap,
        setupComponentMap,
    } = useMemo(() => {
        const setupApiMap: Record<string, InjectApiOption> = {};
        const bridgeOnMap: Record<string, ApiCallback> = {};
        const setupComponentMap = [];
        
        if (typeof setup === 'function') {
            setup({
                injectApi: (apiName, options) => {
                    options = typeof options === 'function' ? {
                        type: 'function',
                        key: apiName,
                        cb: options,
                    } : options;
                    
                    const {cb, ...apiOption} = options;
                    setupApiMap[options.key] = apiOption;
                    bridgeOnMap[options.key] = cb;
                },
                injectComponents: (name, js, css) => {
                    setupComponentMap.push({
                        name,
                        js,
                        css,
                    });
                }
            });
        }

        return {
            setupApiMap,
            bridgeOnMap,
            setupComponentMap,
        }
    }, []);
    
    const {editorSetup} = useEditor(editor);
    
    // 安装依赖
    const setupCommunication = useCallback((bridge: IframeBridge) => {
        bridge.on('page-resize', (size: [number, number]) => {
            if (onSizeChange) {
                onSizeChange(size);
            }
        });
        // 组件内报错
        bridge.on('error-boundary', (data) => {
            if (onError) {
                onError(new Error(data));
            }
        });
        // 代码编译完成（无论是否成功）
        bridge.on('code-compiled', (data) => {
            if (typeof onCompiled === 'function') {
                const {
                    error,
                    waitTime,
                    ...compiledInfo
                } = data || {};
                const res = Object.keys(compiledInfo).length > 0 ? compiledInfo : null;
                onCompiled(res, {waitTime, error});
            }
        });
        // 初次组件加载完三方资源后的回调
        bridge.on('component-ready', () => {
            if (typeof onReady === 'function') {
                onReady();
            }
        });
        // 监听iframe内的点击事件
        bridge.on('component-click', () => {
            if (typeof onComponentClick === 'function') {
                onComponentClick();
            }
        });
        bridge.on('fb-keyboard', (data) => {
            if (typeof onKeyboard === 'function') {
                const {key, options} = data || {};
                onKeyboard(key, options);
            }
        });
        // 监听滚轮事件
        bridge.on('fb-wheel-event', (data) => {
            if (typeof onWheel === 'function') {
                const {deltaYList} = data || {};
                if (deltaYList?.length > 0) {
                    deltaYList.forEach(deltaY => {
                        onWheel({deltaY});
                    });
                }
            }
        });
        // 局部编辑
        editorSetup(bridge);
        // 错误重试
        bridge.on('error-retry', (error: Error) => {
            if (typeof onErrorRetry === 'function') {
                onErrorRetry(error);
            }
        });
        // 监听业务方自定义的api
        for (const [key, cb] of Object.entries(bridgeOnMap)) {
            bridge.registerApi(key, data => {
                return cb(...data);
            });
        }
        // 监听三方插件的iframe事件，例如编辑器操作需要发送op
        ClientPlugins.forEach(plugin => {
            plugin.setupBridge(bridge);
        })
    }, []);
    
    const { bridge, connectToIframe, connectionState } = useIframeBridge(setupCommunication);
    
    // 组件api
    useImperativeHandle(ref, () => ({
        cleanRangeTrigger: async () => {
            if (!bridge) {
                return;
            }
            return bridge.callApi('clean-range-trigger', null);
        }
    }), [bridge]);
    
    // 获取当前连接信息
    const {isConnected = false, error} = connectionState || {};
    
    // iframe加载完成后初始化连接
    const handleIframeLoad = useCallback(async () => {
        if (!iframeRef.current?.contentWindow) {
            return;
        }
        try {
            // 连接到iframe
            const bridge = await connectToIframe(iframeRef.current.contentWindow);
            // 握手完成后发一个初始化请求
            bridge.waitForReady().then(() => {
                bridge.emit('bridge-initial', {
                    builderId,
                    defaultCompiledComponent,
                    registerApi: setupApiMap,
                    registerComponents: setupComponentMap,
                    // 用于重置内部h-screen与min-h-screen这两个样式的高度值: 100vh；100vh会引起自适应高度失效
                    viewSize: adaptHeight ? [window.innerWidth, window.innerHeight] : undefined,
                });
            });
        } catch (error) {
            console.error('连接iframe失败:', error);
        }
    }, [builderId, connectToIframe, defaultCompiledComponent, setupApiMap]);
    
    // 代码变更时触发
    useEffect(() => {
        // if (!bridge || !isConnected || error || editor?.state === 'edit') {
        if (!bridge || !isConnected || error) {
            return;
        }
        bridge.emit('render-update', {
            compiledComponent,
            code,
            renderProps,
            editorState: editor?.state,
            hScreen,
            showErrorRetry,
        });
    }, [compiledComponent, bridge, code, renderProps, isConnected, error, editor?.state, hScreen, showErrorRetry]);

    useEffect(() => {
        // 把 emitter 传给外部，用户随心搭内部监听外部事件
        // 外部可通过 bridge.emit 方法触发一个事件
        if (passEmitter) {
            passEmitter(bridge);
        }

        return () => {
            if (passEmitter) {
                passEmitter(null);
            }
        };
    }, [passEmitter, bridge]);
    
    return (
        <iframe
            ref={iframeRef}
            src={iframeUrl}
            style={{
                width: '100%',
                height: '100%',
                border: 'none',
            }}
            onLoad={handleIframeLoad}
            className={'iframe-sandbox ' + (typeof className === 'string' ? className : '')}
            title="随心搭组件"
            // sandbox="allow-scripts allow-forms allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation"
            {...restProps}
        />
    );
});
